/*
 * Copyright 2025 Sisters Inspire Sdn Bhd
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * SYS-3414 — where every key of the v1 flat IHS response goes in v2.
 *
 * A consumer migrating off `GET /lender/ihs/:ihsId` has one question per key:
 * what do I read instead? This answers it for all 662, and it answers
 * "nothing, and here is why" for the ones with no destination — which is the
 * half a rename table would leave out and the half that loses data.
 *
 * WHAT THIS IS NOT. It is not a runtime shim and nothing reads it at request
 * time. It is a migration instrument: consumers read it once, while porting.
 * It goes away with the v1 surface.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE FAILURE MODE IT WAS BUILT AGAINST, because it bit twice while it was
 * being built and both times the output looked finished.
 *
 * 1. An address that does not resolve. The first draft emitted 20 addresses
 *    naming fields the category does not declare — 18 invented outright, and
 *    2 from substring matching, one of which sent `tangibleAssets` to
 *    `nonCurrentAssetIntangibleAssets`: the opposite fact, spelled almost the
 *    same. All 20 were confidently formatted.
 *
 * 2. An address that resolves and is still wrong. Resolving proves a field
 *    EXISTS somewhere; it does not prove it is the RIGHT somewhere. 38 keys
 *    carried a field name declared by more than one category, and matching on
 *    name alone silently took whichever category came first in the JSON — it
 *    put `companyName` on `financial-statement`, and split one bank statement
 *    across two categories while its own siblings went to a third.
 *
 * So this map records HOW each address was derived, in `via`, and the test
 * beside it re-proves closure against the live registry on every run. The two
 * `via` values that mean "somebody wrote this down" are `wide-column-feeder`
 * and `form-intake-fieldmap`; `name-equality` means "the names matched and
 * only one category declares it", which is weaker and is why an ambiguous
 * name is refused rather than guessed.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * A `mapped` entry is not a promise that data is there today. It is the
 * address. `mapped-pending-build` says the address is real and nothing writes
 * it yet — the field is declared, the table or the instance is not. Read
 * `retired` as "this key has no v2 destination", never as "this data is
 * gone": several retired keys are live data arriving under a different name
 * (`city` is submitted as `permanentcity`), and one, `ssmIncorporatedDate`,
 * is a dead column with live readers (SYS-3419).
 */

import raw from './data/v1-migration-map.json' with { type: 'json' };

/** What happens to a v1 key in v2. */
export type V1Disposition =
  /** One v2 address. Read it there. */
  | 'mapped'
  /** TWO attestors, one legacy column. v2 keeps both; the disagreement is the signal. */
  | 'mapped-fanout'
  /** The address is well-formed and nothing writes it yet. */
  | 'mapped-pending-build'
  /** Not adapter data — it moved to another surface, named in `surface`. */
  | 'relocated'
  /** v1 envelope machinery the v2 shape supersedes. */
  | 'structural'
  /** No v2 destination. NOT the same as "the data is gone" — read `note`. */
  | 'retired'
  /** A destination is wanted and no canonical field exists to be the destination. */
  | 'vocabulary-gap'
  /** More than one defensible destination; picking one needs an owner, not a lookup. */
  | 'needs-decision'
  /**
   * SYS-3604 — v1 serves it, the v2 successor deliberately refuses it, and
   * there is no other destination. Distinct from `relocated`, which promises
   * a surface: six keys carried that promise naming an endpoint that had
   * removed them in review, and `flatRecordFromView` quoted it back to the
   * caller. The honest answer here is "nowhere, by design".
   */
  | 'withheld';

/** How the address was derived. The first two are authored; the rest are weaker. */
export type V1ResolutionSource =
  /** finsys-api's wideColumnFeeders.ts — which document category feeds the column. */
  | 'wide-column-feeder'
  /** A form-intake manifest's fieldMap — which form field produces the canonical one. */
  | 'form-intake-fieldmap'
  /** The category registry's own `legacyName` bridge. */
  | 'legacy-name'
  /** Names matched and exactly one category declares it. Refused when ambiguous. */
  | 'name-equality'
  /** The SYS-2499 column audit's NOT-ADAPTER classes. */
  | 'column-audit'
  /** The six-consumer sweep — a verdict no rule can derive. */
  | 'consumer-sweep'
  /** Authored against evidence cited in `note`. */
  | 'hand-authored';

/**
 * Where a value lives in v2.
 *
 * `instanceKey` names ONE instance. `instanceKeyPrefix` means the v1 key became
 * N instances and the reader must collect all of them — a shape change, not a
 * rename (a document-pointer column held a JSON array of paths; document-intake
 * keys one instance per file). AT MOST ONE OF THE TWO IS EVER PRESENT, and a
 * test asserts it, because an address carrying both would leave a consumer to
 * pick — and picking `instanceKey` on a fan-out silently reads one file of N.
 */
export interface V1Address {
  readonly category: string;
  readonly field: string;
  readonly instanceKey?: string;
  readonly instanceKeyPrefix?: string;
}

export interface V1MigrationEntry {
  readonly disposition: V1Disposition;
  readonly via?: V1ResolutionSource;
  /** Present on `mapped`, `mapped-pending-build`. */
  readonly address?: V1Address;
  /** Present on `mapped-fanout`, always length >= 2. */
  readonly addresses?: ReadonlyArray<V1Address>;
  /** Present on `relocated` — the surface that now serves this key. */
  readonly surface?: string;
  readonly note?: string;
  readonly reason?: string;
}

interface RawMap {
  readonly schemaVersion: string;
  readonly entries: Readonly<Record<string, V1MigrationEntry>>;
}

const map = raw as unknown as RawMap;

const DISPOSITIONS: ReadonlySet<string> = new Set<V1Disposition>([
  'mapped',
  'mapped-fanout',
  'mapped-pending-build',
  'relocated',
  'withheld',
  'structural',
  'retired',
  'vocabulary-gap',
  'needs-decision',
]);

let validated = false;

/**
 * The cast above is a claim, not a check — TypeScript verifies nothing about
 * imported JSON at runtime, so a map carrying a disposition outside the union
 * would type-check as exhaustive and then fall through every consumer's
 * `switch` as `undefined`. A key silently treated as having no destination is
 * the precise shape of loss this map exists to prevent.
 *
 * So it is checked, and checked LAZILY rather than at module load. Every
 * production consumer of this package imports it for the category registry and
 * never touches the migration map; an import-time throw would turn a bad data
 * file into a total outage for code that does not read the data. Validating on
 * first access puts the failure where the data is actually used, and costs one
 * pass over 662 entries the first time somebody asks.
 */
function assertValidated(): void {
  if (validated) return;
  const bad: string[] = [];
  for (const [key, entry] of Object.entries(map.entries)) {
    if (!DISPOSITIONS.has(entry.disposition)) bad.push(`${key}: "${entry.disposition}"`);
  }
  if (bad.length > 0) {
    throw new Error(
      `v1-migration-map.json declares ${bad.length} unknown disposition(s), so this map cannot ` +
        `be read safely: ${bad.slice(0, 5).join(', ')}${bad.length > 5 ? ', …' : ''}. ` +
        `Regenerate it with scripts/build-v1-migration-map.py.`,
    );
  }
  validated = true;
}

/** Draft until the v1 surface retires; the shape may still move. */
export const V1_MIGRATION_MAP_VERSION: string = map.schemaVersion;

/** Every v1 response key this map accounts for. */
export function v1MigrationKeys(): ReadonlyArray<string> {
  assertValidated();
  return Object.keys(map.entries);
}

/** The disposition of one v1 key, or null if the map does not know it. */
export function v1MigrationEntry(key: string): V1MigrationEntry | null {
  assertValidated();
  return Object.prototype.hasOwnProperty.call(map.entries, key) ? map.entries[key]! : null;
}

/**
 * Every address a key resolves to — 0, 1, or (fanout) 2+.
 *
 * Returning an array rather than an optional single address is deliberate:
 * a caller that handles only the single case silently drops the second
 * attestor of a fanout key and reports one source's answer as "the" answer.
 */
export function v1Addresses(key: string): ReadonlyArray<V1Address> {
  const e = v1MigrationEntry(key);
  if (!e) return [];
  if (e.addresses) return e.addresses;
  return e.address ? [e.address] : [];
}

/** Keys sharing a disposition — for a consumer auditing its own reads. */
export function v1KeysByDisposition(d: V1Disposition): ReadonlyArray<string> {
  assertValidated();
  return Object.keys(map.entries).filter((k) => map.entries[k]!.disposition === d);
}

// ── Reverse lookup (SYS-3334) ────────────────────────────────────────

/**
 * SYS-3334 F1 (round 2). The dispositions BOTH reverse bridges — `reverseIndex`
 * (backing `v1KeyForAddress`) and `legacyBaseNameIndex` (backing
 * `v1LegacyBaseNameOf`) — admit as carrying a real destination. Before this
 * constant existed the two were two independently-typed disposition checks
 * that happened to agree; they silently stopped agreeing the moment
 * `legacyBaseNameIndex` was written admitting only `'mapped'` while
 * `reverseIndex` already admitted `mapped-fanout` and `mapped-pending-build`
 * too. The gap was `incorporatedDate` (`mapped-fanout`, SYS-2722's two
 * attestors, `company-profile`/`company-registration`): `reverseIndex`
 * answered it, `legacyBaseNameIndex` answered null, and every SSM / Form 9
 * view-path row silently lost the "Incorporated Date" column despite the
 * value being right there in the view. One constant now backs both bridges
 * so they cannot disagree again — see `legacyBaseNameIndex`'s own comment
 * for why `mapped-pending-build` belongs here too (a label is right whether
 * or not the address is written yet).
 */
const REVERSIBLE_DISPOSITIONS: ReadonlySet<V1Disposition> = new Set<V1Disposition>([
  'mapped',
  'mapped-fanout',
  'mapped-pending-build',
]);

/**
 * The reverse of `v1Addresses`: given a place on the canonical plane, the v1
 * key that used to hold it — or null.
 *
 * WHY IT EXISTS. The instance-shaped detail processor has to render a v2 view
 * with the labels and groupings the v1 panel had, or the flag flip on the
 * consumer is a redesign rather than a migration. Both label and grouping are
 * keyed by the LEGACY column name (the display-name catalogue and the form
 * spec), so the processor needs canonical → legacy. This map is the only
 * complete statement of that relationship: 771 keys, generated and proven.
 *
 * WHEN IT ANSWERS NULL, AND WHY THAT IS THE RIGHT ANSWER.
 *   - No v1 key ever held this address: a canonical-only field (the alt-data
 *     categories, or a registry field newer than the wide table). The caller
 *     falls back to the registry's own names.
 *   - More than one v1 key maps here with no instanceKey to tell them apart:
 *     the T-slot families (bankBalanceT1..T6 → closingBalance). Those are
 *     document-extraction fields, rendered by the file-field tables and never
 *     by the detail panel, so the ambiguity never reaches a caller that needs
 *     it resolved. Returning null rather than a guess is deliberate: a guess
 *     would label six documents' balances "T1".
 *
 * `instanceKey` matters: `contactValue` at `email` was `email`, at `mobile` was
 * `mobilePhoneNo`. An address without one matches only entries without one.
 */
export function v1KeyForAddress(
  category: string,
  field: string,
  instanceKey?: string,
): string | null {
  const idx = reverseIndex();
  const hits = idx.get(reverseKey(category, field, instanceKey));
  return hits && hits.length === 1 ? hits[0]! : null;
}

function reverseKey(category: string, field: string, instanceKey?: string): string {
  // NUL cannot appear in a category id, field name or instance key.
  return `${category}\u0000${field}\u0000${instanceKey ?? ''}`;
}

let reverse: Map<string, string[]> | null = null;
let keyedAddresses: Set<string> | null = null;

/**
 * Whether the map holds ANY instance-keyed entry for (category, field). When it
 * does not, an instance key carries no discriminating power for that field and
 * a keyed lookup that misses may fall back to the keyless entry. When it does
 * (`contactValue`: email vs mobile), no such fallback is offered.
 */
export function v1AddressHasKeyedEntries(category: string, field: string): boolean {
  reverseIndex();
  return keyedAddresses!.has(`${category}\u0000${field}`);
}

function reverseIndex(): Map<string, string[]> {
  if (reverse) return reverse;
  assertValidated();
  const idx = new Map<string, string[]>();
  const keyed = new Set<string>();
  for (const [key, entry] of Object.entries(map.entries)) {
    // The reverse question is "what LABEL did this address carry", and the
    // label is right whether or not anything writes the address yet — so
    // `mapped-pending-build` is admitted here (the forward lookup is where a
    // not-yet-written destination is a wrong answer). Eleven addresses; the
    // day they are written, they keep their v1 names instead of falling into
    // General. `REVERSIBLE_DISPOSITIONS` — this bridge's disposition set and
    // `legacyBaseNameIndex`'s must never diverge again; see that constant's doc.
    if (!REVERSIBLE_DISPOSITIONS.has(entry.disposition)) continue;
    for (const a of entry.addresses ?? (entry.address ? [entry.address] : [])) {
      // A prefix address is a fan-out over N instances (document pointers);
      // no single instance corresponds to the v1 key, so it is not reversible.
      if (a.instanceKeyPrefix) continue;
      const k = reverseKey(a.category, a.field, a.instanceKey);
      const list = idx.get(k);
      if (list) list.push(key);
      else idx.set(k, [key]);
      if (a.instanceKey !== undefined) keyed.add(`${a.category}\u0000${a.field}`);
    }
  }
  reverse = idx;
  keyedAddresses = keyed;
  return idx;
}

// ── Legacy base-name bridge (SYS-3334) ──────────────────────────────

/**
 * The same T-slot suffix regex `scripts/build-v1-migration-map.py`'s own
 * PERIOD constant uses.
 *
 * The `(PriorYear)?` branch is UNREACHABLE here as of the map correction
 * (SYS-3334 round 2): the six `…PriorYearT{n}` keys (`netProfitPriorYearT1`,
 * …, `totalEquityPriorYearT3`) are now `needs-decision` — see
 * `scripts/build-v1-migration-map.py`'s `PRIOR_YEAR_COMPARATIVE` override and
 * this package's CHANGELOG — so no `mapped` / `mapped-fanout` /
 * `mapped-pending-build` key contains "PriorYear" for this regex to match
 * against. It stays literal-identical to the Python generator's PERIOD
 * constant rather than forking the two (the doc line above is the whole
 * point: one regex, read the same way in both places), and it regains a live
 * match the moment either candidate resolution ships a PriorYear key back
 * into `mapped`.
 */
const TIME_PERIOD_SUFFIX = /(PriorYear)?T[1-9]$/;

function legacyBaseNameKey(category: string, field: string): string {
  return category + ' ' + field;
}

let legacyBaseNames: Map<string, string> | null = null;

/**
 * Builds the (category, field) -> legacy BASE name index, lazily and once
 * (see `assertValidated`'s reasoning for why lazy: importing this module must
 * not throw for a consumer that never reads the map).
 *
 * SYS-3334 F1 (round 2 — CRITICAL, fixed here). This used to admit ONLY
 * `mapped` entries — a narrower set than `reverseIndex`'s (which already
 * admitted `mapped-fanout` and `mapped-pending-build`), and the two bridges
 * silently disagreeing is exactly the failure `REVERSIBLE_DISPOSITIONS`
 * exists to prevent (see its doc). The concrete casualty: `incorporatedDate`
 * is `mapped-fanout` (`company-profile`/`companyIncorporationDate` AND
 * `company-registration`/`companyIncorporationDate` — SYS-2722's two
 * attestors), so this index returned null for both addresses,
 * `instanceRowsFromView` `continue`d at the null, and the SSM / Form 9
 * instance rows silently lost the "Incorporated Date" column while the value
 * sat right there in the view. Now uses the SAME disposition set
 * `reverseIndex` does, so the two bridges cannot diverge again.
 *
 * A `mapped-fanout` entry contributes EVERY one of its `addresses`, not just
 * one — a fanout is two attestors of the SAME v1 key, and both attestors'
 * (category, field) pairs shared that key's base name in v1 (the wide column
 * held one value; either extractor could have written it).
 *
 * Restricted to addresses that carry neither `instanceKey` nor
 * `instanceKeyPrefix`. That is a DIFFERENT multiplicity from the one this
 * function bridges: several DISTINCT v1 keys landing on one (category, field)
 * because an INSTANCE tells them apart — `bankStatements`/`consentForm`/…
 * all onto `document-intake`/`pathInDms` (an instanceKeyPrefix each), or
 * `email`/`mobile`/`office`/`emergency` onto `applicant-contact`/
 * `contactValue` (an instanceKey each). Stripping a T-suffix from THOSE keys
 * would collide unrelated legacy names into one slot — `bankStatements` and
 * `consentForm` share no T-suffix to strip, so the naive index would record
 * two different "base names" for one address and throw on a case that is not
 * a defect at all. Excluding any address with an instance discriminator
 * leaves exactly the T-slot families (`bankBalanceT1..T6`, `revenueT1..T3`,
 * `incorporatedDate`'s two-attestor fanout, …) — verified empirically against
 * the live (post-F1, post-map-correction, post-SYS-3570) map: 620
 * instance-less addresses across `mapped` / `mapped-fanout` /
 * `mapped-pending-build`, converging to 253 distinct (category, field) bases,
 * 0 conflicts — the property this function relies on, and the conflict guard
 * below is what would catch a regression of it. (Was 616/252 before SYS-3570
 * gave the four `tangibleAssets*` keys an address; they share one base, so
 * they add four addresses and one base.)
 */
function legacyBaseNameIndex(): Map<string, string> {
  if (legacyBaseNames) return legacyBaseNames;
  assertValidated();
  const idx = new Map<string, string>();
  for (const [key, entry] of Object.entries(map.entries)) {
    if (!REVERSIBLE_DISPOSITIONS.has(entry.disposition)) continue;
    const addrs = entry.addresses ?? (entry.address ? [entry.address] : []);
    const baseName = key.replace(TIME_PERIOD_SUFFIX, '');
    for (const addr of addrs) {
      if (addr.instanceKey !== undefined || addr.instanceKeyPrefix !== undefined) continue;
      const k = legacyBaseNameKey(addr.category, addr.field);
      const existing = idx.get(k);
      if (existing !== undefined && existing !== baseName) {
        // A registry defect, not a data shape this function is meant to
        // tolerate: two T-slot families at the SAME instance-less address
        // disagreeing on their shared base name means the map (or the T-slot
        // catalog entries it was built from) no longer agrees with itself.
        // This is also the guard the brief calls out for a fanout whose TWO
        // addresses disagree with each other — `addrs` is walked in the same
        // loop, so a fanout entry hits this exact check across its own pair.
        throw new Error(
          `v1LegacyBaseNameOf(${addr.category}, ${addr.field}): two different legacy base names map here — ` +
            `"${existing}" and "${baseName}" (from v1 key "${key}"). Regenerate the map with ` +
            `scripts/build-v1-migration-map.py and check the T-slot catalog entries at this address.`,
        );
      }
      idx.set(k, baseName);
    }
  }
  legacyBaseNames = idx;
  return idx;
}

/**
 * The T-suffix-stripped legacy name every `mapped` v1 key at this (category,
 * field) address shares — e.g. `finxtract-bank-statement`/`closingBalance` →
 * `bankBalance` (from `bankBalanceT1`..`bankBalanceT6`, all stripping to the
 * same base). This is the bridge `buildFileFieldTablesFromView` needs: it
 * feeds a `CanonicalView`'s values into `buildFileFieldTablesFromInstances`,
 * which is keyed by the legacy BASE column name, not the canonical one.
 *
 * Deliberately NOT `v1KeyForAddress`, which answers null on exactly this
 * T-slot case — it is answering a different question ("which ONE v1 key was
 * this"), and there is no single answer once a field has six T-slot
 * siblings. This function only needs the shared BASE, which (by construction
 * — see `legacyBaseNameIndex`'s comment) every slot in the family agrees on.
 *
 * Null when no instance-less REVERSIBLE v1 key (`mapped`, `mapped-fanout`, or
 * `mapped-pending-build` — the same set `legacyBaseNameIndex` admits, so this
 * function and `v1KeyForAddress` cannot disagree about which addresses have a
 * legacy name at all) ever held this (category, field) — a canonical-only
 * field: an alt-data category the wide table never had, or a registry field
 * newer than it (e.g. `financial-statement`/`consolidated`,
 * `financial-statement`/`localNo`). SYS-3334 F1 (round 2): this used to say
 * `mapped` only, which was wrong for exactly one case — `mapped-fanout` — and
 * that case was `incorporatedDate`, so this function answered null for a
 * field that very much had a wide column.
 */
export function v1LegacyBaseNameOf(category: string, field: string): string | null {
  return legacyBaseNameIndex().get(legacyBaseNameKey(category, field)) ?? null;
}

// ── Fanout attestor index (SYS-3334 H-1, round 5) ────────────────────

let fanoutAddressCache: Map<string, { key: string; index: number }> | null = null;

/**
 * Builds the (category, field) -> { fanout's own v1 key, address INDEX }
 * index, lazily and once — one entry per address of every `mapped-fanout`
 * entry (today: `incorporatedDate`'s two). `index` is the 0-based position
 * in the entry's OWN `addresses` array, the same order
 * `flatRecordFromView`'s `mapped-fanout` branch already treats as
 * authoritative ("place the FIRST address, in map order, that has a value").
 */
function fanoutAddressIndex(): Map<string, { key: string; index: number }> {
  if (fanoutAddressCache) return fanoutAddressCache;
  assertValidated();
  const idx = new Map<string, { key: string; index: number }>();
  for (const [key, entry] of Object.entries(map.entries)) {
    if (entry.disposition !== 'mapped-fanout') continue;
    (entry.addresses ?? []).forEach((addr, index) => {
      idx.set(legacyBaseNameKey(addr.category, addr.field), { key, index });
    });
  }
  fanoutAddressCache = idx;
  return idx;
}

/**
 * Whether (category, field) is one attestor of a `mapped-fanout` entry, and
 * if so its 0-based position in the map's own `addresses` order — null for
 * anything else (a plain `mapped` address, or no address at all).
 *
 * WHY IT EXISTS. `fieldProvenanceFromView` (`ihs-processing.ts`) has to tell
 * "two DIFFERENT attestors of ONE fanout key disagreeing" apart from "two
 * instances of the SAME category colliding on one slot" — SYS-2722 designed
 * the fanout so BOTH attestors survive (that is the whole point of a
 * two-address entry), so the first is not a collision at all; the second
 * is. This is the one piece of information that distinction needs, and it
 * did not exist anywhere in this module before H-1 — `v1LegacyBaseNameOf`
 * answers the shared BASE NAME both addresses resolve to, which is exactly
 * what makes the two writes LOOK like a collision (same key) without saying
 * whether they are one.
 */
export function v1FanoutAddressOf(category: string, field: string): { key: string; index: number } | null {
  return fanoutAddressIndex().get(legacyBaseNameKey(category, field)) ?? null;
}
